// Web Audio synth for authentic PC-speaker beeps, key clicks, HDD/floppy grind.
// Nothing plays until user interacts (browser autoplay policy compliance).

let ctx = null;
let master = null;
let muted = false;
let hddNoise = null;
let hddGain = null;

function ensureCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 0.35;
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

export function primeAudio() {
  ensureCtx();
}

export function setMuted(v) {
  muted = !!v;
  if (master) master.gain.value = muted ? 0 : 0.35;
}

export function isMuted() {
  return muted;
}

// PC-speaker style square-wave beep
export function beep({ freq = 880, duration = 0.12, gain = 0.5, type = "square" } = {}) {
  const c = ensureCtx();
  if (!c || muted) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, c.currentTime);
  g.gain.exponentialRampToValueAtTime(gain, c.currentTime + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + duration);
  osc.connect(g).connect(master);
  osc.start();
  osc.stop(c.currentTime + duration + 0.02);
}

// Short high-pitch key click (mechanical clack style)
export function keyClick() {
  const c = ensureCtx();
  if (!c || muted) return;
  const buf = c.createBuffer(1, c.sampleRate * 0.035, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    const t = i / data.length;
    data[i] = (Math.random() * 2 - 1) * (1 - t) * 0.6;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 2200;
  bp.Q.value = 4;
  const g = c.createGain();
  g.gain.value = 0.5;
  src.connect(bp).connect(g).connect(master);
  src.start();
}

// Longer keyboard clack (space/enter)
export function keyClack() {
  const c = ensureCtx();
  if (!c || muted) return;
  const buf = c.createBuffer(1, c.sampleRate * 0.06, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    const t = i / data.length;
    data[i] = (Math.random() * 2 - 1) * (1 - t) * 0.75;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1400;
  bp.Q.value = 3;
  const g = c.createGain();
  g.gain.value = 0.55;
  src.connect(bp).connect(g).connect(master);
  src.start();
}

// HDD/floppy grinding: filtered noise loop with subtle modulation
export function startHDD(intensity = 0.4) {
  const c = ensureCtx();
  if (!c || muted) return;
  if (hddNoise) return;
  const buf = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1);
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 380;
  bp.Q.value = 2.4;
  const g = c.createGain();
  g.gain.value = intensity;
  // Slow tremolo LFO to give it that mechanical seek feeling
  const lfo = c.createOscillator();
  lfo.type = "sine";
  lfo.frequency.value = 6.2;
  const lfoGain = c.createGain();
  lfoGain.gain.value = intensity * 0.6;
  lfo.connect(lfoGain).connect(g.gain);
  src.connect(bp).connect(g).connect(master);
  src.start();
  lfo.start();
  hddNoise = { src, lfo };
  hddGain = g;
}

export function stopHDD() {
  if (!hddNoise) return;
  const c = ensureCtx();
  try {
    hddGain.gain.linearRampToValueAtTime(0, c.currentTime + 0.12);
    setTimeout(() => {
      try { hddNoise.src.stop(); } catch (_) {}
      try { hddNoise.lfo.stop(); } catch (_) {}
      hddNoise = null;
      hddGain = null;
    }, 150);
  } catch (_) {
    hddNoise = null;
    hddGain = null;
  }
}

// BIOS long POST beep
export function postBeep() {
  beep({ freq: 900, duration: 0.28, gain: 0.42, type: "square" });
  setTimeout(() => beep({ freq: 900, duration: 0.06, gain: 0.35 }), 380);
}

// Error tri-tone (used for BSOD)
export function errorTone() {
  beep({ freq: 220, duration: 0.5, gain: 0.5, type: "sawtooth" });
  setTimeout(() => beep({ freq: 165, duration: 0.6, gain: 0.5, type: "sawtooth" }), 60);
}

// Short confirmation tone (menu open)
export function selectBeep() {
  beep({ freq: 1400, duration: 0.05, gain: 0.3 });
}

// Modem-ish handshake blip when launching HEISENBERG
export function launchTone() {
  const c = ensureCtx();
  if (!c || muted) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(660, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(1760, c.currentTime + 0.5);
  g.gain.setValueAtTime(0.0001, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.35, c.currentTime + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.55);
  osc.connect(g).connect(master);
  osc.start();
  osc.stop(c.currentTime + 0.6);
}
